import { useEffect, useState } from 'react';
import { Button, Input, Alert, AlertDescription, Spinner } from '@/components/ui';
import { Smartphone, Mail, KeyRound, LifeBuoy } from 'lucide-react';
import { challengeStart, verifyMfa, passkeyChallenge, type MfaMethod, type ChallengeMethod } from '@/services/mfaService';

interface Props {
  orgId?: string;
  methods: MfaMethod[];
  /** Recovery codes exist for this account — offer them as a way through. */
  recoveryAvailable?: boolean;
  onVerified: (token: string, expiresAtMs: number, sudoToken: string, sudoExpiresAtMs: number) => void;
}

const META: Record<ChallengeMethod, { label: string; Icon: typeof Smartphone }> = {
  passkey: { label: 'Passkey', Icon: KeyRound },
  totp: { label: 'Authenticator app', Icon: Smartphone },
  email: { label: 'Email code', Icon: Mail },
  recovery: { label: 'Recovery code', Icon: LifeBuoy },
};

/**
 * Does this device have a platform authenticator (Touch ID / Windows Hello)?
 * A passkey whose only transport is `hybrid` lives on a *phone*, so offering it
 * as the default on a laptop starts a QR + Bluetooth dance that most people
 * abandon — and if it's the only prominent option, that's a lockout. We can't
 * see transports client-side, so we use local-authenticator availability as the
 * proxy for "is passkey likely to just work here".
 */
function usePlatformAuthenticator(): boolean | null {
  const [available, setAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    const api = (window as any).PublicKeyCredential;
    if (!api?.isUserVerifyingPlatformAuthenticatorAvailable) { setAvailable(false); return; }
    api.isUserVerifyingPlatformAuthenticatorAvailable()
      .then((ok: boolean) => { if (!cancelled) setAvailable(ok); })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; };
  }, []);
  return available;
}

export default function MfaChallenge({ orgId, methods, recoveryAvailable, onVerified }: Props) {
  const platformAuth = usePlatformAuthenticator();
  const enrolled = (['passkey', 'totp', 'email'] as MfaMethod[]).filter((m) => methods.includes(m));
  const ordered: ChallengeMethod[] = [...enrolled, ...(recoveryAvailable ? (['recovery'] as ChallengeMethod[]) : [])];

  const [method, setMethod] = useState<ChallengeMethod | null>(null);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Default to whatever is most likely to succeed *on this device*. Passkey
  // only leads when a local authenticator can answer it.
  useEffect(() => {
    if (method || platformAuth === null || ordered.length === 0) return;
    const preferred = platformAuth
      ? ordered[0]
      : ordered.find((m) => m !== 'passkey') ?? ordered[0];
    setMethod(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformAuth, methods.join(','), recoveryAvailable]);

  // Auto-send the email code when that method becomes active.
  useEffect(() => {
    if (method !== 'email') return;
    setEmailSent(false); setError('');
    challengeStart('email', orgId).then(() => setEmailSent(true)).catch(() => setError('Could not send a code. Try again.'));
  }, [method, orgId]);

  const finish = (r: Awaited<ReturnType<typeof verifyMfa>>) =>
    onVerified(r.mfaToken, r.expiresAtMs, r.sudoToken, r.sudoExpiresAtMs);

  const submitCode = async () => {
    if (!method) return;
    setError(''); setLoading(true);
    try {
      finish(await verifyMfa({ method, code: code.trim(), rememberDevice: remember }, orgId));
    } catch (e: any) {
      setError(e?.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const usePasskey = async () => {
    setError(''); setLoading(true);
    try {
      const assertion = await passkeyChallenge(orgId);
      finish(await verifyMfa({ method: 'passkey', assertion, rememberDevice: remember }, orgId));
    } catch (e: any) {
      // The ceremony fails entirely in-browser for a phone-bound passkey the
      // user can't complete, so name the alternative instead of a bare error.
      setError(
        e?.name === 'NotAllowedError'
          ? 'That passkey may be on another device. Try a different method below.'
          : e?.message || 'Passkey verification failed. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  if (!method) return <div className="flex justify-center py-6"><Spinner size="sm" /></div>;

  const isRecovery = method === 'recovery';

  return (
    <div className="space-y-4">
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {method === 'passkey' ? (
        <Button className="w-full" onClick={usePasskey} disabled={loading}>
          {loading ? <Spinner size="sm" /> : (<><KeyRound className="w-4 h-4 mr-2" />Use your passkey</>)}
        </Button>
      ) : (
        <div className="space-y-3">
          {method === 'email' && (
            <p className="text-sm text-muted-foreground">{emailSent ? 'We sent a 6-digit code to your email.' : 'Sending a code to your email…'}</p>
          )}
          {isRecovery && (
            <p className="text-sm text-muted-foreground">Enter one of the recovery codes you saved. Each code works once.</p>
          )}
          <Input
            label={isRecovery ? 'Recovery code' : 'Verification code'}
            inputMode={isRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            placeholder={isRecovery ? 'XXXXX-XXXXX' : '123456'}
            value={code}
            onChange={(e) => setCode(isRecovery
              ? e.target.value.toUpperCase().slice(0, 11)
              : e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={loading}
          />
          <Button className="w-full" onClick={submitCode} disabled={loading || code.length < (isRecovery ? 10 : 6)}>
            {loading ? <Spinner size="sm" /> : 'Verify'}
          </Button>
        </div>
      )}

      {/* A redeemed recovery code means the user is on an unfamiliar device by
          definition — don't quietly extend 30-day trust to it. */}
      {!isRecovery && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-border" />
          Remember this device for 30 days
        </label>
      )}

      {ordered.length > 1 && (
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Or use another method:</p>
          <div className="flex flex-wrap gap-2">
            {ordered.filter((m) => m !== method).map((m) => {
              const { label, Icon } = META[m];
              return (
                <button key={m} onClick={() => { setMethod(m); setCode(''); setError(''); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-muted">
                  <Icon className="w-4 h-4" /> {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
